/* ============================================================
   SD GLASS — pill rail controller
   Opens when sd-reveal adds .is-in. Long panes clamp with a
   More toggle. Tapping the active pill shuts the panel.

   Perf (Oct 2026):
   - Auto-rotation pauses while the section is off-screen or the
     tab is hidden, and resumes exactly where it left off (same
     remaining time, progress line frozen mid-fill). Each rotation
     animates the panel height, which re-lays out the whole page
     below it — it now only happens while someone can see it.
   - paint() reads every position/height first, then writes, so a
     rotation costs one layout pass instead of several forced ones.
   - The progress line is driven by the Web Animations API, so it
     can pause/resume without a forced reflow to restart it.
   - The overflow check measures all panes in one pass.
   ============================================================ */
(function () {
  'use strict';

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var CHEV = '<svg viewBox="0 0 12 8" fill="none" aria-hidden="true">' +
             '<path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.6" ' +
             'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function initPills(scope) {
    var roots = (scope || document).querySelectorAll('[data-sd-pills]:not([data-sdg-booted])');

    Array.prototype.forEach.call(roots, function (root) {
      root.setAttribute('data-sdg-booted', '1');

      var rail   = root.querySelector('.sd-pills__rail');
      var glider = root.querySelector('.sd-pills__glider');
      var fill   = glider ? glider.querySelector('i') : null;
      var panel  = root.querySelector('.sd-pills__panel');
      var track  = root.querySelector('.sd-pills__track');
      var panes  = [].slice.call(root.querySelectorAll('.sd-pills__pane'));
      var btns   = [].slice.call(root.querySelectorAll('.sd-pills__btn'));
      var dots   = [].slice.call(root.querySelectorAll('.sd-pills__dot'));

      if (!panel || !track || !panes.length) return;

      var rotate  = root.getAttribute('data-auto-rotate') === 'true' && !REDUCE;
      var autoOpn = root.getAttribute('data-auto-open') === 'true';
      var iv      = parseInt(root.getAttribute('data-interval'), 10) || 6000;

      var i = 0, w = 0, timer = null, stopped = false, open = false, shut = false;

      /* rotation clock — lets a pause resume with the exact time left */
      var running = false, paused = false, armAt = 0, left = iv;
      var fillAnim = null;
      var visible = true;

      /* ── wrap each pane's content so it can be clamped ── */
      panes.forEach(function (p) {
        if (p.querySelector('.sd-pills__body')) return;
        var body = document.createElement('div');
        body.className = 'sd-pills__body';
        while (p.firstChild) body.appendChild(p.firstChild);
        p.appendChild(body);
      });

      function clampLimit() {
        var v = getComputedStyle(document.documentElement).getPropertyValue('--sdg-clamp');
        return parseInt(v, 10) || 210;
      }

      /* ── add a More button only where content overflows ──
         All panes are measured in ONE pass (unclamp all → read all → restore all)
         instead of one forced layout per pane. */
      function checkOverflow() {
        var limit = clampLimit();
        var bodies = panes.map(function (p) { return p.querySelector('.sd-pills__body'); });
        var prev = bodies.map(function (b) { return b ? b.style.maxHeight : ''; });
        var k;

        for (k = 0; k < bodies.length; k++) if (bodies[k]) bodies[k].style.maxHeight = 'none';
        var natural = bodies.map(function (b) { return b ? b.scrollHeight : 0; });
        for (k = 0; k < bodies.length; k++) if (bodies[k]) bodies[k].style.maxHeight = prev[k];

        panes.forEach(function (p, n) {
          var body = bodies[n];
          if (!body) return;

          var expanded = p.getAttribute('data-clamped') === 'false';
          var more = p.querySelector('.sd-pills__more');

          if (natural[n] <= limit + 24) {
            p.removeAttribute('data-clamped');
            if (more) more.remove();
            return;
          }

          if (!more) {
            more = document.createElement('button');
            more.type = 'button';
            more.className = 'sd-pills__more';
            more.setAttribute('aria-expanded', 'false');
            more.innerHTML = '<span>Read more</span>' + CHEV;
            more.addEventListener('click', function () {
              var nowOpen = p.getAttribute('data-clamped') === 'false';
              p.setAttribute('data-clamped', nowOpen ? 'true' : 'false');
              more.setAttribute('aria-expanded', String(!nowOpen));
              more.querySelector('span').textContent = nowOpen ? 'Read more' : 'Show less';
              stop();
              setTimeout(setHeight, 30);
              setTimeout(setHeight, 320);
              setTimeout(setHeight, 660);
            });
            p.appendChild(more);
          }

          if (!expanded) p.setAttribute('data-clamped', 'true');
        });
      }

      function measure() {
        w = panel.clientWidth;
        panes.forEach(function (p) { p.style.width = w + 'px'; });
      }

      function moveGlider() {
        if (!glider || !btns.length || !rail) return;
        var b  = btns[i];
        var rb = rail.getBoundingClientRect();
        var bb = b.getBoundingClientRect();
        glider.style.width = bb.width + 'px';
        glider.style.transform = 'translate3d(' + (bb.left - rb.left + rail.scrollLeft - 5) + 'px,0,0)';
        glider.style.opacity = 1;
      }

      function setHeight() {
        if (shut) { panel.style.height = '0px'; return; }
        panel.style.height = panes[i].offsetHeight + 'px';
      }

      /* READ everything first, then WRITE everything — one layout per rotation */
      function paint() {
        /* ---- reads ---- */
        var g = null, h = 0, scrollTo = null, b;

        if (glider && btns.length && rail) {
          b = btns[i];
          var rb = rail.getBoundingClientRect();
          var bb = b.getBoundingClientRect();
          g = { w: bb.width, x: bb.left - rb.left + rail.scrollLeft - 5 };
        }

        if (!shut) h = panes[i].offsetHeight;

        if (btns.length && rail) {
          b = btns[i];
          var ol = b.offsetLeft, ow = b.offsetWidth;
          var sl = rail.scrollLeft, cw = rail.clientWidth;
          if (ol + ow > sl + cw || ol < sl) scrollTo = Math.max(0, ol - 20);
        }

        /* ---- writes ---- */
        track.style.transform = 'translate3d(' + (-i * w) + 'px,0,0)';
        btns.forEach(function (bt, n) { bt.setAttribute('aria-selected', n === i); });
        dots.forEach(function (d, n) {
          n === i ? d.setAttribute('aria-current', 'true') : d.removeAttribute('aria-current');
        });
        panes.forEach(function (p, n) { p.setAttribute('data-active', n === i); });

        if (g) {
          glider.style.width = g.w + 'px';
          glider.style.transform = 'translate3d(' + g.x + 'px,0,0)';
          glider.style.opacity = 1;
        }

        panel.style.height = (shut ? 0 : h) + 'px';

        if (scrollTo !== null) {
          rail.scrollTo({ left: scrollTo, behavior: REDUCE ? 'auto' : 'smooth' });
        }
      }

      function go(n, human) {
        i = (n + panes.length) % panes.length;
        if (human) stop();
        paint();
        if (!stopped && rotate && open && !shut) arm();
      }

      /* ── red progress line ── */
      function startFill() {
        if (!fill) return;
        if (fillAnim) { fillAnim.cancel(); fillAnim = null; }
        if (fill.animate) {
          fill.classList.remove('sdg-run');
          fillAnim = fill.animate(
            [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
            { duration: iv, easing: 'linear', fill: 'forwards' }
          );
        } else {
          /* very old browsers: original CSS class restart */
          fill.classList.remove('sdg-run');
          void fill.offsetWidth;
          fill.style.animationDuration = iv + 'ms';
          fill.style.animationPlayState = '';
          fill.classList.add('sdg-run');
        }
      }

      function onScreen() { return visible && !document.hidden; }

      function next() {
        running = false;
        go(i + 1, false);
      }

      function arm() {
        clearTimeout(timer);
        running = true;
        paused = false;
        left = iv;
        armAt = performance.now();
        startFill();
        timer = setTimeout(next, iv);
        if (!onScreen()) pauseRun();
      }

      /* off-screen or hidden tab: freeze the clock and the progress line */
      function pauseRun() {
        if (!running || paused) return;
        paused = true;
        clearTimeout(timer);
        left = Math.max(0, left - (performance.now() - armAt));
        if (fillAnim) fillAnim.pause();
        else if (fill) fill.style.animationPlayState = 'paused';
      }

      /* back on screen: carry on with exactly the time that was left */
      function resumeRun() {
        if (!running || !paused || stopped || shut || !open) return;
        paused = false;
        armAt = performance.now();
        if (fillAnim) fillAnim.play();
        else if (fill) fill.style.animationPlayState = 'running';
        timer = setTimeout(next, left);
      }

      function stop() {
        stopped = true;
        running = false;
        paused = false;
        clearTimeout(timer);
        if (fillAnim) { fillAnim.cancel(); fillAnim = null; }
        if (fill) {
          fill.classList.remove('sdg-run');
          fill.style.transform = 'scaleX(0)';
        }
      }

      function setShut(v) {
        shut = v;
        root.classList.toggle('is-shut', v);
        if (v) { stop(); panel.style.height = '0px'; }
        else { measure(); paint(); }
      }

      function openPanel() {
        if (open) return;
        open = true;
        root.classList.add('is-open');
        measure();
        checkOverflow();
        paint();
        requestAnimationFrame(function () { measure(); checkOverflow(); paint(); });
        if (rotate && !stopped) arm();
      }

      /* ── pause while off-screen / tab hidden ── */
      if ('IntersectionObserver' in window) {
        var vio = new IntersectionObserver(function (entries) {
          visible = entries[entries.length - 1].isIntersecting;
          if (onScreen()) resumeRun(); else pauseRun();
        }, { rootMargin: '100px 0px 100px 0px' });
        vio.observe(root);
      }
      document.addEventListener('visibilitychange', function () {
        if (onScreen()) resumeRun(); else pauseRun();
      });

      /* ── pills: switch, or toggle shut if already active ── */
      btns.forEach(function (b) {
        b.addEventListener('click', function () {
          var n = parseInt(b.getAttribute('data-i'), 10);
          if (!open) { openPanel(); go(n, true); return; }
          if (n === i) { setShut(!shut); stop(); return; }
          if (shut) setShut(false);
          go(n, true);
        });
      });

      dots.forEach(function (d) {
        d.addEventListener('click', function () {
          if (!open) openPanel();
          if (shut) setShut(false);
          go(parseInt(d.getAttribute('data-i'), 10), true);
        });
      });

      /* ── swipe ─────────────────────────────── */
      var sx = 0, sy = 0, dx = 0, drag = false, lock = null, pid = null;

      panel.addEventListener('pointerdown', function (e) {
        if (shut) return;
        if (e.target.closest('.sd-pills__more')) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        drag = true; lock = null; dx = 0; pid = e.pointerId;
        sx = e.clientX; sy = e.clientY;
        measure();
      });

      panel.addEventListener('pointermove', function (e) {
        if (!drag || e.pointerId !== pid) return;
        var mx = e.clientX - sx, my = e.clientY - sy;
        if (lock === null) {
          if (Math.abs(mx) < 6 && Math.abs(my) < 6) return;
          lock = Math.abs(mx) > Math.abs(my) ? 'x' : 'y';
          if (lock === 'x') {
            root.classList.add('is-drag');
            stop();
            try { panel.setPointerCapture(pid); } catch (err) {}
          }
        }
        if (lock !== 'x') return;
        e.preventDefault();
        dx = mx;
        if ((i === 0 && dx > 0) || (i === panes.length - 1 && dx < 0)) dx *= 0.32;
        track.style.transform = 'translate3d(' + (-i * w + dx) + 'px,0,0)';
      }, { passive: false });

      function release() {
        if (!drag) return;
        drag = false;
        root.classList.remove('is-drag');
        if (lock === 'x') {
          var t = Math.max(40, w * 0.18);
          if (dx < -t) go(i + 1, true);
          else if (dx > t) go(i - 1, true);
          else paint();
        }
        lock = null; dx = 0;
      }
      panel.addEventListener('pointerup', release);
      panel.addEventListener('pointercancel', release);
      panel.addEventListener('pointerleave', release);

      if (rail) {
        rail.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowRight') { openPanel(); if (shut) setShut(false); go(i + 1, true); }
          if (e.key === 'ArrowLeft')  { openPanel(); if (shut) setShut(false); go(i - 1, true); }
        });
      }

      if (window.ResizeObserver) {
        var ro = new ResizeObserver(function () {
          if (!open || shut) return;
          measure(); setHeight(); moveGlider();
        });
        panes.forEach(function (p) { ro.observe(p); });
      }

      var rt = null;
      window.addEventListener('resize', function () {
        if (!open || shut) return;
        clearTimeout(rt);
        rt = setTimeout(function () { measure(); checkOverflow(); paint(); }, 120);
      });
      window.addEventListener('load', function () {
        if (open && !shut) { measure(); checkOverflow(); paint(); }
      });

      /* ── chain the open to sd-reveal's is-in ─ */
      if (!autoOpn) { openPanel(); return; }

      var host = root.classList.contains('sd-reveal') ? root : root.closest('.sd-reveal');

      if (!host || host.classList.contains('is-in') || REDUCE) {
        setTimeout(openPanel, REDUCE ? 0 : 260);
        return;
      }

      var mo = new MutationObserver(function () {
        if (!host.classList.contains('is-in')) return;
        mo.disconnect();
        setTimeout(openPanel, 420);
      });
      mo.observe(host, { attributes: true, attributeFilter: ['class'] });

      setTimeout(function () { if (!open) openPanel(); }, 6000);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initPills(document); });
  } else {
    initPills(document);
  }

  document.addEventListener('shopify:section:load', function (e) { initPills(e.target); });

  window.SDGlass = { pills: initPills };
})();